import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, map, mergeMap, of, takeUntil } from 'rxjs';
import { clearAllState } from '../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { PatientReport } from '../../models/patient-report';
import { PatientDocumentsService } from '../../services/documents/documents.service';
import { PatientNotesService } from '../../services/notes/notes.service';
import * as actions from '../actions/reports.actions';
import { selectPageNumber, selectPageSize } from '../selectors/reports.selectors';

@Injectable()
export class ReportsEffects {
  private actions$ = inject(Actions);
  private patientDocumentsService = inject(PatientDocumentsService);
  private patientNotesService = inject(PatientNotesService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getReports$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getReports, actions.nextPage, actions.retryPage),
      concatLatestFrom(() => [
        this.store.select(selectPatientId),
        this.store.select(selectPageNumber),
        this.store.select(selectPageSize),
      ]),
      mergeMap(([, patientId, pageNumber, pageSize]) => {
        const page = {
          pageNumber: pageNumber ?? 1,
          pageSize: pageSize ?? 30,
        };
        const documents$ = page.pageNumber === 1 ? this.patientDocumentsService.getPatientDocuments(patientId) : of([]);

        const notes$ = this.patientNotesService.postPatientNotes(patientId, page.pageNumber, page.pageSize);

        return forkJoin({
          patientDocuments: documents$,
          patientNotes: notes$,
        }).pipe(
          takeUntil(this.cancelRequest$),
          map(({ patientDocuments, patientNotes }) => {
            const combinedReports: PatientReport[] = [
              ...patientDocuments.map(doc => ({
                description: doc.description,
                subtext: doc.comment,
                uploadedByName: doc.uploadedByName,
                reportType: 'document',
                documentTypeId: doc.documentTypeId,
                reportId: doc.patientDocumentId,
                reportDateUtc: new Date(doc.uploadedOnUtc),
                isRead: doc.isRead,
                // url: doc.documentUrl,
                // extension:
                //   doc.extension === null ? null : doc.extension?.startsWith('.') ? doc.extension : `.${doc.extension}`,
              })),
              ...patientNotes.map(note => ({
                description: note.noteTypeDescription,
                subtext: null,
                uploadedByName: note.completedBy,
                reportType: 'note',
                documentTypeId: 4,
                reportId: note.patientNoteId,
                reportDateUtc: new Date(note.noteDateUtc),
                url: note.noteUrl,
                extension: '.html',
                isRead: note.isRead,
              })),
            ];

            const sortedReports = combinedReports.sort((a, b) => b.reportDateUtc.getTime() - a.reportDateUtc.getTime());

            return actions.getReportsSuccess({
              reports: sortedReports,
            });
          }),
          catchError(err => {
            // Pass the HttpErrorResponse whole, per ADR-014. Unwrapping to `err.error.errors` stores a
            // falsy value when the body carries a present-but-empty errors field, which skips the
            // error branch and renders "No reports on file" — and it destroys the type
            // classifyLoadError needs to tell a timeout from an offline failure.
            return of(actions.getReportsFailure({ error: err }));
          })
        );
      })
    );
  });

  recordPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.recordReportPrintEvent),
      mergeMap(({ patientId, reportId, reportType }) => {
        const request$ =
          reportType === 'document'
            ? this.patientDocumentsService.postPrintEvent(patientId, reportId)
            : this.patientNotesService.postPrintEvent(patientId, reportId);

        return request$.pipe(
          map(() => actions.recordReportPrintEventSuccess()),
          catchError(err => of(actions.recordReportPrintEventFailure({ error: err })))
        );
      })
    );
  });
}
